import { useLocation, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { gruposPermisosAgregar, gruposPermisosListar } from '../api/grupos.api'
import { useState, useEffect } from 'react'

import { Alert, Modal } from "../../../shared/ui";
import { obtenerLocalStorage } from '../../../shared/utils/funciones'
import { CONST_KEY_USUARIO_AUTENTICADO } from '../../../app/constants/constanst-valores'
import { CONST_RUTA_GRUPOS_LISTAR } from '../../../app/constants/constants-rutas-codigos'
import { TablaSkeleton } from "../../../shared/ui/TablaSkeleton";

// Un NIVEL del árbol de permisos. Al entrar a un módulo con submódulos se abre este
// mismo componente en un modal ANIDADO (Radix Dialog apilado) -> cerrar con la "X" o
// "Cancelar" baja SOLO un nivel (no cierra todo). El nivel RAÍZ va dentro del modal del
// router; su "Cancelar" cierra el modal completo (vuelve al listado).
const PermisosNivel = ({ grupo, compania, superior, titulo, nombreGrupo, initialModulos, onCerrar }: any) => {

    const usuarioAutenticado = obtenerLocalStorage(CONST_KEY_USUARIO_AUTENTICADO)

    const [listaModulos, setListaModulos] = useState<any[]>(initialModulos || [])
    const [isLoading, setIsLoading] = useState(false)
    const [mensaje, setMensaje] = useState('')
    const [mostrar, setMostrar] = useState(false)
    const [tipoAlerta, setTipoAlerta] = useState<any>("")
    // Submódulo abierto en el nivel de arriba (modal anidado). {codigo, desc, modulos}
    const [submodulo, setSubmodulo] = useState<any>(null)

    const { register, handleSubmit, reset } = useForm<any>({ defaultValues: { modulos: [] } })

    const formatear = (modulos: any[]) =>
        (modulos || []).map((m: any) => ({ codigo: m?.codigo, desc: m?.desc, estado: m?.estado === "A" }))

    const cargar = async () => {
        if (String(grupo) === "") { onCerrar?.(); return }
        setIsLoading(true)
        const { modulos } = await gruposPermisosListar({
            compania,
            superior,
            grupo,
            tip_usuario: usuarioAutenticado?.tipo,
            token: usuarioAutenticado?.token,
        })
        const fmt = formatear(modulos)
        reset({ modulos: fmt })
        setListaModulos(fmt)
        setIsLoading(false)
    }

    useEffect(() => {
        // Nivel anidado: recibe los módulos ya consultados (evita doble fetch). Raíz: consulta.
        if (initialModulos) reset({ modulos: initialModulos })
        else cargar()
        // carga inicial (una sola vez al montar)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Solo abre el nivel de submódulos si el módulo TIENE submódulos (igual que antes).
    const abrirSubmodulos = async (mod: any) => {
        const { modulos } = await gruposPermisosListar({
            compania,
            superior: mod?.codigo,
            grupo,
            tip_usuario: usuarioAutenticado?.tipo,
            token: usuarioAutenticado?.token,
        })
        const fmt = formatear(modulos)
        if (fmt.length > 0) setSubmodulo({ codigo: mod?.codigo, desc: mod?.desc, modulos: fmt })
    }

    const onGuardar = async (data: any) => {
        const modulos = (data.modulos || [])
            .filter((m: any) => m?.codigo !== undefined)
            .map((m: any) => ({ cod_modulo: m.codigo, estado: m.estado ? "A" : "I" }))

        const { status, message } = await gruposPermisosAgregar({
            cod_grupo: grupo,
            compania,
            modulos,
            token: usuarioAutenticado?.token,
        })

        setMostrar(true)
        setMensaje(message)
        setTipoAlerta(status === 1 ? "success" : "warning")
    }

    return (
        <div className="p-1">
            <h3 className="tw-text-center tw-text-2xl tw-font-semibold tw-text-menu-primario tw-mt-1 tw-mb-3">{titulo}</h3>

            <div className="mb-2">
                <strong>Grupo: </strong>
                <label>{nombreGrupo}</label>
            </div>

            <div className="table-responsive">
                <table className="table table-hover tw-mb-0">
                    <thead className="table-light">
                        <tr>
                            <th scope="col" className="col-2 text-center">Programa</th>
                            <th scope="col" className="col-7 text-center">Módulo</th>
                            <th scope="col" className="col-3 text-center">Permiso</th>
                        </tr>
                    </thead>
                    <tbody className={isLoading ? "" : "tabla-fade-in"}>
                        {
                            (isLoading && !listaModulos?.length) ? (
                                <TablaSkeleton rows={6} cols={3} />
                            ) : (
                                (listaModulos?.length > 0) ? listaModulos.map((g, index) => (
                                    <tr key={g?.codigo}>
                                        <td>{g?.codigo}</td>
                                        {/* El nombre abre los submódulos en un modal ANIDADO (por nivel). */}
                                        <td><button type="button" className="btn btn-link p-0 text-decoration-none" onClick={() => abrirSubmodulos(g)}>{g?.desc}</button></td>
                                        <td>
                                            <div className="form-check d-flex justify-content-center align-items-center">
                                                <input className="form-check-input"
                                                    type="checkbox"
                                                    id={`chk-${superior}-${g?.codigo}`}
                                                    {...register(`modulos.${index}.estado`)}
                                                />
                                            </div>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr><td colSpan={3 as any} className="text-center">No hay módulos</td></tr>
                                )
                            )
                        }
                    </tbody>
                </table>
            </div>

            <div className='mt-3 botones-accion text-center'>
                <button className='btn btn-sm btn-primario col-md-3' onClick={handleSubmit(onGuardar)}>Guardar</button>
                <button className='btn btn-sm btn-secundario col-md-3' onClick={() => onCerrar?.()}>Cancelar</button>
            </div>

            {
                mostrar && (
                    <div className="row mt-3">
                        <div className="col-lg-8 mx-auto text-center alert-error">
                            <Alert severity={tipoAlerta}>{mensaje}</Alert>
                        </div>
                    </div>
                )
            }

            {/* Modal ANIDADO: submódulos del módulo elegido. Cerrar (X/Cancelar) baja UN nivel. */}
            <Modal
                open={!!submodulo}
                size="lg"
                showClose
                dismissable={false}
                onOpenChange={(o) => { if (!o) setSubmodulo(null) }}
            >
                {submodulo && (
                    <PermisosNivel
                        key={submodulo.codigo}
                        grupo={grupo}
                        compania={compania}
                        superior={submodulo.codigo}
                        titulo={submodulo.desc}
                        nombreGrupo={nombreGrupo}
                        initialModulos={submodulo.modulos}
                        onCerrar={() => setSubmodulo(null)}
                    />
                )}
            </Modal>
        </div>
    )
}

export const GrupoPermisos = () => {

    const navigate = useNavigate()
    const location = useLocation()

    const nombreGrupo = location?.state?.nombreGrupo || ""
    const compania = location?.state?.compania || ""
    const grupo = location?.state?.grupo || ""

    // Nivel RAÍZ: vive dentro del modal del router. "Cancelar" (y la "X" del router) cierran
    // el modal completo -> vuelven al listado de grupos. Los niveles de submódulos son modales
    // anidados que se cierran "por niveles".
    return (
        <PermisosNivel
            grupo={grupo}
            compania={compania}
            superior={[0]}
            titulo="Grupo - Permisos"
            nombreGrupo={nombreGrupo}
            onCerrar={() => navigate(CONST_RUTA_GRUPOS_LISTAR)}
        />
    )
}
